import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import BodyHeader from "../../../components/common/CommonBodyHeader";
import CustomInput from "../../../components/common/CustomInput/CustomInput";
import CustomDateTimePicker from "../../../components/common/CustomDateTimePicker/CustomDateTimePicker";
import CustomDatePicker from "../../../components/common/CustomDatePicker/CustomDatePicker";
import CustomTextArea from "../../../components/common/CustomTextArea/CustomTextArea";
import CustomDropdown from "../../../components/common/CustomDropdown/CustomDropdown";

import { backdropLoadingAction } from "../../../redux/features/helperSlice";
import { createTripAsync } from "../../../redux/features/tripSlice";
import {
   getAllDriverAndVehicleAsync,
   getAllLaneAsync,
   getFleetDropdownAsync,
} from "../../../redux/features/dropdownSlice";

const validationSchema = Yup.object().shape({
   vehicle_no: Yup.string().required("Vehicle No is required"),
   drivername: Yup.string().required("Driver name is required"),
   driverno: Yup.string().required("Driver no is required"),
   lane: Yup.string().required("Lane name is required"),
   customername: Yup.string().required("Customer name is required"),
   origin: Yup.string().required("Origin is required"),
   destination: Yup.string().required("Destination is required"),
   ewaybillno: Yup.string().required("E-Way Bill No is required"),
   ewaybill_expiry_date: Yup.string().required("E-Way Bill Expiry is required"),
   invoice_no: Yup.string().required("Invoice No is required"),
   invoice_date: Yup.string().required("Invoice Date is required"),
   invoice_value: Yup.string().required("Invoice Value is required"),
   customer_lrno: Yup.string().required("Customer/Eisaku LR No is required"),
   vehicle_reported_date: Yup.string().required("Vehicle Reported Date is required"),
   vehicle_disptch_date: Yup.string().required("Vehicle Dispatch Date is required"),
   actual_diesel_provided: Yup.number()
      .typeError("Must be a number")
      .required("Actual Diesel Provided is required"),
   actual_adblue_provided: Yup.number()
      .typeError("Must be a number")
      .required("Actual Adblue Provided is required"),
   actual_driver_trip_advance: Yup.number()
      .typeError("Must be a number")
      .required("Actual Driver Trip Advance is required"),
});

const INITIAL_VALUES = {
   vehicle_no: "",
   drivername: "",
   driverno: "",
   lane: "",
   customername: "",
   origin: "",
   destination: "",
   ewaybillno: "",
   ewaybill_expiry_date: "",
   invoice_no: "",
   invoice_date: "",
   invoice_value: "",
   customer_lrno: "",
   vehicle_reported_date: "",
   vehicle_disptch_date: "",
   actual_diesel_provided: "",
   actual_adblue_provided: "",
   actual_driver_trip_advance: "",
   actual_miscellaneous_expense: "",
   remarks: "",
};

export default function ContractualTripAdd() {
   const dispatch = useDispatch();
   const navigate = useNavigate();

   // Display labels for read-only auto-filled fields coming from dropdown selection
   const [displayValues, setDisplayValues] = useState({
      customername: "",
      origin: "",
      destination: "",
      driverno: "",
   });

   const dropdownData = useSelector((state) => state.dropdownData);

   // Vehicle dropdown options
   const vehicleOptions = (dropdownData?.vehicle || []).map((v) => ({
      label: v.vehicle_no,
      value: v.id,
   }));

   // Driver dropdown options
   const driverOptions = (dropdownData?.driver || []).map((d) => ({
      label: d.name,
      value: d.id,
      mobile: d.mobile,
      name: d.name,
   }));
   // Lane dropdown options
   const laneOptions = (dropdownData?.lane || []).map((l) => ({
      label: l.name,
      value: l.id,
      customer_master: l.customer_master,
      origin_location_master: l.origin_location_master,
      destination_location_master: l.destination_location_master,
      customer_master_id: l.customer_master_id,
   }));

   useEffect(() => {
      dispatch(getAllDriverAndVehicleAsync());
      dispatch(getAllLaneAsync());
   }, [dispatch]);

   const formik = useFormik({
      initialValues: INITIAL_VALUES,
      validationSchema,
      onSubmit: (values) => {

         const payload = {
            model_id: "2",
            action_id: "1",
            lanetype: "contactual",
            vehicle_no: values.vehicle_no,
            drivername: values.drivername,
            driverno: values.driverno,
            customername: values.customername,
            lane: values.lane,
            ewaybillno: values.ewaybillno,
            ewaybill_expiry_date: values.ewaybill_expiry_date,
            invoice_no: values.invoice_no,
            invoice_date: values.invoice_date,
            invoice_value: values.invoice_value,
            Customer_lrno: values.customer_lrno,
            vehicle_reported_date: values.vehicle_reported_date,
            vehicle_disptch_date: values.vehicle_disptch_date,
            actual_diesel_provided: values.actual_diesel_provided,
            actual_adblue_provided: values.actual_adblue_provided,
            actual_driver_trip_advance: values.actual_driver_trip_advance,
            actual_miscellaneous_expense: values.actual_miscellaneous_expense,
            remarks: values.remarks,
         };

         dispatch(backdropLoadingAction(true));
         dispatch(
            createTripAsync({
               data: payload,
               done: () => {
                  dispatch(backdropLoadingAction(false));
                  toast.success("Trip created successfully!");
                  navigate("/trip/contractual/view");
               },
               err: (error) => {
                  dispatch(backdropLoadingAction(false));
                  toast.error(error || "Something went wrong!");
               },
            }),
         );
      },
   });

   const { values, errors, touched, setFieldValue, handleSubmit } = formik;

   return (
      <div>
         <BodyHeader title="Create Trip" />
         <form className="p-3 shadow-lg" onSubmit={handleSubmit}>
            {/* ── SECTION 1: General Information ── */}
            <div className="card card-primary">
               <div className="card-header">
                  <h3 className="card-title">General Information</h3>
               </div>
               <div className="card-body">
                  <div className="row">
                     {/* Vehicle No */}
                     <div className="col-lg-4">
                        <label className="text-bold">
                           Vehicle No. <span className="text-danger">*</span>
                        </label>
                        <CustomDropdown
                           optionData={vehicleOptions}
                           name="vehicle_no"
                           values={values.vehicle_no ? [values.vehicle_no] : []}
                           onChange={(val) => setFieldValue("vehicle_no", val?.[0]?.value ?? "")}
                           placeholder="Select vehicle"
                        />
                        {touched.vehicle_no && errors.vehicle_no && (
                           <small className="text-danger">{errors.vehicle_no}</small>
                        )}
                     </div>

                     {/* Driver Name */}
                     <div className="col-lg-4">
                        <label className="text-bold">
                           Driver Name <span className="text-danger">*</span>
                        </label>
                        <CustomDropdown
                           optionData={driverOptions}
                           name="drivername"
                           values={values.drivername ? [values.drivername] : []}
                           onChange={(val) => {
                              const selected = driverOptions.find(
                                 (d) => d.value === val?.[0].value,
                              );
                              setFieldValue("drivername", selected?.value ?? "");
                              console.log("selected", selected);
                              setFieldValue("driverno", selected?.mobile ?? "");
                              setDisplayValues((prev) => ({
                                 ...prev,
                                 driverno: selected?.mobile ?? "",
                              }));
                           }}
                           placeholder="Select driver"
                        />
                        {touched.drivername && errors.drivername && (
                           <small className="text-danger">{errors.drivername}</small>
                        )}
                     </div>

                     {/* Driver No (auto-filled) */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Driver No."
                           id="driverno"
                           placeholder="Auto-filled from driver"
                           disabled={true}
                           value={displayValues.driverno || values.driverno}
                           name="driverno"
                        />
                        {touched.driverno && errors.driverno && (
                           <small className="text-danger">{errors.driverno}</small>
                        )}
                     </div>
                  </div>
               </div>
            </div>

            {/* ── SECTION 2: Lane Information ── */}
            <div className="card card-primary">
               <div className="card-header">
                  <h3 className="card-title">Lane Information</h3>
               </div>
               <div className="card-body">
                  <div className="row">
                     {/* Lane Name */}
                     <div className="col-lg-4">
                        <label className="text-bold">
                           Lane Name <span className="text-danger">*</span>
                        </label>
                        <CustomDropdown
                           optionData={laneOptions}
                           name="lane"
                           values={values.lane ? [values.lane] : []}
                           onChange={(val) => {
                              const selected = laneOptions.find(
                                 (l) => l.value === val?.[0].value,
                              );
                              setFieldValue("lane", selected?.value ?? "");
                              // Auto-fill read-only fields from lane data
                              setFieldValue(
                                 "customername",
                                 selected?.customer_master?.id ?? "",
                              );
                              setFieldValue(
                                 "origin",
                                 selected?.origin_location_master?.id ?? "",
                              );
                              setFieldValue(
                                 "destination",
                                 selected?.destination_location_master?.id ?? "",
                              );
                              setDisplayValues((prev) => ({
                                 ...prev,
                                 customername: selected?.customer_master?.name ?? "",
                                 origin: selected?.origin_location_master?.name ?? "",
                                 destination:
                                    selected?.destination_location_master?.name ?? "",
                              }));
                           }}
                           placeholder="Select lane"
                        />
                        {touched.lane && errors.lane && (
                           <small className="text-danger">{errors.lane}</small>
                        )}
                     </div>

                     {/* Customer Name (auto-filled) */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Customer Name"
                           id="customername"
                           placeholder="Auto-filled from lane"
                           disabled={true}
                           value={displayValues.customername}
                           name="customername"
                        />
                        {touched.customername && errors.customername && (
                           <small className="text-danger">{errors.customername}</small>
                        )}
                     </div>

                     {/* Origin (auto-filled) */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Origin"
                           id="origin"
                           placeholder="Auto-filled from lane"
                           disabled={true}
                           value={displayValues.origin}
                           name="origin"
                        />
                        {touched.origin && errors.origin && (
                           <small className="text-danger">{errors.origin}</small>
                        )}
                     </div>

                     {/* Destination (auto-filled) */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Destination"
                           id="destination"
                           placeholder="Auto-filled from lane"
                           disabled={true}
                           value={displayValues.destination}
                           name="destination"
                        />
                        {touched.destination && errors.destination && (
                           <small className="text-danger">{errors.destination}</small>
                        )}
                     </div>

                     {/* E-Way Bill No */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="E-Way Bill No."
                           id="ewaybillno"
                           placeholder="E-Way Bill No."
                           value={values.ewaybillno}
                           name="ewaybillno"
                           onChange={(e) => setFieldValue("ewaybillno", e.target.value)}
                           require
                        />
                        {touched.ewaybillno && errors.ewaybillno && (
                           <small className="text-danger">{errors.ewaybillno}</small>
                        )}
                     </div>

                     {/* E-Way Bill Expiry */}
                     <div className="col-lg-4">
                        <CustomDateTimePicker
                           label="E-Way Bill Expiry Date & Time"
                           name="ewaybill_expiry_date"
                           id="ewaybill_expiry_date"
                           value={values.ewaybill_expiry_date}
                           onChange={(e) =>
                              setFieldValue("ewaybill_expiry_date", e.target.value)
                           }
                           require
                        />
                        {touched.ewaybill_expiry_date && errors.ewaybill_expiry_date && (
                           <small className="text-danger">
                              {errors.ewaybill_expiry_date}
                           </small>
                        )}
                     </div>

                     {/* Invoice No */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Invoice No."
                           id="invoice_no"
                           placeholder="Invoice No."
                           value={values.invoice_no}
                           name="invoice_no"
                           onChange={(e) => setFieldValue("invoice_no", e.target.value)}
                           require
                        />
                        {touched.invoice_no && errors.invoice_no && (
                           <small className="text-danger">{errors.invoice_no}</small>
                        )}
                     </div>

                     {/* Invoice Date */}
                     <div className="col-lg-4">
                        <CustomDatePicker
                           label="Invoice Date"
                           name="invoice_date"
                           id="invoice_date"
                           value={values.invoice_date}
                           onChange={(e) => setFieldValue("invoice_date", e.target.value)}
                           require
                        />
                        {touched.invoice_date && errors.invoice_date && (
                           <small className="text-danger">{errors.invoice_date}</small>
                        )}
                     </div>

                     {/* Invoice Value */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Invoice Value"
                           id="invoice_value"
                           placeholder="Invoice Value"
                           inputType="number"
                           value={values.invoice_value}
                           name="invoice_value"
                           onChange={(e) =>
                              setFieldValue("invoice_value", e.target.value)
                           }
                           require
                        />
                        {touched.invoice_value && errors.invoice_value && (
                           <small className="text-danger">{errors.invoice_value}</small>
                        )}
                     </div>

                     {/* Customer / Eisaku LR No */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Customer / Eisaku LR No."
                           id="customer_lrno"
                           placeholder="Customer / Eisaku LR No."
                           value={values.customer_lrno}
                           name="customer_lrno"
                           onChange={(e) =>
                              setFieldValue("customer_lrno", e.target.value)
                           }
                           require
                        />
                        {touched.customer_lrno && errors.customer_lrno && (
                           <small className="text-danger">{errors.customer_lrno}</small>
                        )}
                     </div>

                     {/* Vehicle Reported Date */}
                     <div className="col-lg-4">
                        <CustomDateTimePicker
                           label="Vehicle Reported Date & Time"
                           name="vehicle_reported_date"
                           id="vehicle_reported_date"
                           value={values.vehicle_reported_date}
                           onChange={(e) =>
                              setFieldValue("vehicle_reported_date", e.target.value)
                           }
                           require
                        />
                        {touched.vehicle_reported_date &&
                           errors.vehicle_reported_date && (
                              <small className="text-danger">
                                 {errors.vehicle_reported_date}
                              </small>
                           )}
                     </div>

                     {/* Vehicle Dispatch Date */}
                     <div className="col-lg-4">
                        <CustomDateTimePicker
                           label="Vehicle Dispatch Date & Time"
                           name="vehicle_disptch_date"
                           id="vehicle_disptch_date"
                           value={values.vehicle_disptch_date}
                           onChange={(e) =>
                              setFieldValue("vehicle_disptch_date", e.target.value)
                           }
                           require
                        />
                        {touched.vehicle_disptch_date && errors.vehicle_disptch_date && (
                           <small className="text-danger">
                              {errors.vehicle_disptch_date}
                           </small>
                        )}
                     </div>
                  </div>
               </div>
            </div>

            {/* ── SECTION 3: Expense Information ── */}
            <div className="card card-primary">
               <div className="card-header">
                  <h3 className="card-title">Expense Information</h3>
               </div>
               <div className="card-body">
                  <div className="row">
                     {/* Actual Diesel Provided */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Actual Diesel Provided (Ltr)"
                           id="actual_diesel_provided"
                           placeholder="E.g 100 Ltr."
                           inputType="number"
                           value={values.actual_diesel_provided}
                           name="actual_diesel_provided"
                           onChange={(e) =>
                              setFieldValue("actual_diesel_provided", e.target.value)
                           }
                           require
                        />
                        {touched.actual_diesel_provided &&
                           errors.actual_diesel_provided && (
                              <small className="text-danger">
                                 {errors.actual_diesel_provided}
                              </small>
                           )}
                     </div>

                     {/* Actual Adblue Provided */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Actual Adblue Provided (Ltr)"
                           id="actual_adblue_provided"
                           placeholder="E.g 100 Ltr."
                           inputType="number"
                           value={values.actual_adblue_provided}
                           name="actual_adblue_provided"
                           onChange={(e) =>
                              setFieldValue("actual_adblue_provided", e.target.value)
                           }
                           require
                        />
                        {touched.actual_adblue_provided &&
                           errors.actual_adblue_provided && (
                              <small className="text-danger">
                                 {errors.actual_adblue_provided}
                              </small>
                           )}
                     </div>

                     {/* Actual Driver Trip Advance */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Actual Driver Trip Advance (Rs.)"
                           id="actual_driver_trip_advance"
                           placeholder="E.g 100 Rs."
                           inputType="number"
                           value={values.actual_driver_trip_advance}
                           name="actual_driver_trip_advance"
                           onChange={(e) =>
                              setFieldValue("actual_driver_trip_advance", e.target.value)
                           }
                           require
                        />
                        {touched.actual_driver_trip_advance &&
                           errors.actual_driver_trip_advance && (
                              <small className="text-danger">
                                 {errors.actual_driver_trip_advance}
                              </small>
                           )}
                     </div>

                     {/* Actual Miscellaneous Expense */}
                     <div className="col-lg-4">
                        <CustomInput
                           label="Actual Miscellaneous Expense (Rs.)"
                           id="actual_miscellaneous_expense"
                           placeholder="E.g 100 Rs."
                           inputType="number"
                           value={values.actual_miscellaneous_expense}
                           name="actual_miscellaneous_expense"
                           onChange={(e) =>
                              setFieldValue(
                                 "actual_miscellaneous_expense",
                                 e.target.value,
                              )
                           }
                        />
                     </div>

                     {/* Remarks */}
                     <div className="col-lg-12">
                        <CustomTextArea
                           label="Remarks"
                           id="remarks"
                           name="remarks"
                           placeholder="Remarks..."
                           value={values.remarks}
                           onChange={(e) => setFieldValue("remarks", e.target.value)}
                        />
                     </div>
                  </div>
               </div>
            </div>

            {/* Submit */}
            <div className="col-12 mt-4 text-center mb-4">
               <button className="btn btn-primary px-5 py-2" type="submit">
                  <h6 className="mb-0">Create Trip</h6>
               </button>
               <button
                  className="btn btn-secondary ml-3 px-5 py-2"
                  type="button"
                  onClick={() => navigate("/trip/contractual/view")}
               >
                  <h6 className="mb-0">Cancel</h6>
               </button>
            </div>
         </form>
      </div>
   );
}
